import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { partnersCustomersAPI } from '../../services/api';

const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.allSettled([partnersCustomersAPI.getAll('partner'), partnersCustomersAPI.getAll('customer')]);
      if (p.status === 'fulfilled') setPartners(p.value.data || []);
      if (c.status === 'fulfilled') setCustomers(c.value.data || []);
    } catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const resetForm = () => { setEditing(null); setForm({}); };

  const startNew = (type) => {
    setEditing('new');
    setForm({ name: '', logo: '', type, order: (type === 'partner' ? partners : customers).length + 1 });
  };

  const startEdit = (item) => { setEditing(item._id); setForm({ ...item }); };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    const payload = { ...form, order: Number(form.order) || 0 };
    delete payload._id;
    try {
      if (editing === 'new') { await partnersCustomersAPI.create(payload); toast.success('Added'); }
      else { await partnersCustomersAPI.update(editing, payload); toast.success('Updated'); }
      resetForm(); load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await partnersCustomersAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const renderList = (items, type) => (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={() => startNew(type)}><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <div className="space-y-2">
        {items.map(item => (
          <Card key={item._id}>
            <CardContent className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                {item.logo && <img src={item.logo} alt={item.name} className="w-20 h-8 object-contain bg-white border rounded p-0.5" />}
                <div>
                  <h3 className="font-medium text-gray-900">{item.name}</h3>
                  <p className="text-xs text-gray-500">Order: {item.order}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(item)}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(item._id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-center py-8 text-gray-500">No entries yet.</p>}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl">
      {editing && (
        <Card className="mb-6 border-emerald-200" data-testid="pc-form">
          <CardHeader><CardTitle>{editing === 'new' ? `Add ${form.type === 'partner' ? 'Partner' : 'Customer'}` : 'Edit'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><label className="text-sm font-medium mb-1 block">Name *</label><Input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Logo URL</label><Input value={form.logo || ''} onChange={e => setForm({...form, logo: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Order</label><Input type="number" value={form.order || ''} onChange={e => setForm({...form, order: e.target.value})} /></div>
            </div>
            {form.logo && <img src={form.logo} alt="Preview" className="h-10 object-contain" />}
            <div className="flex gap-2">
              <Button onClick={handleSave}><Save className="w-4 h-4 mr-1" /> Save</Button>
              <Button variant="outline" onClick={resetForm}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="partners">
        <TabsList className="mb-4">
          <TabsTrigger value="partners">Partners ({partners.length})</TabsTrigger>
          <TabsTrigger value="customers">Customers ({customers.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="partners">{renderList(partners, 'partner')}</TabsContent>
        <TabsContent value="customers">{renderList(customers, 'customer')}</TabsContent>
      </Tabs>
    </div>
  );
};

export default PartnersManager;
